import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { eventBus } from '@/utils/core/eventBus';
import { log } from '@/utils/core/vueLogger.js';

class DataSyncService {
	constructor() {
		this.isInitialized = false;
		this.syncInProgress = false;
		this.syncCallbacks = [];
		this.accountImages = new Set();
		this.criticalData = new Map();
		this.mainSyncCompleted = false;
		this.backgroundSyncCompleted = false;
		this.lastReferenceSyncTs = 0;

		this.config = {
			maxRetries: 3,
			retryDelay: 1000,
			batchSize: 10,
			maxCacheAge: 7 * 24 * 60 * 60 * 1000,
			preloadImages: true,
		};

		this.initSyncListener();
	}

	initSyncListener() {
		window.addEventListener('message', (event) => {
			if (event.origin !== window.location.origin) return;

			if (event.data && event.data.type === 'SYNC_COMPLETED') {
				log('info', 'DataSync', `📨 Received sync completion message for app: ${event.data.data.app}`);

				if (event.data.data.app === 'ALL') {
					this.mainSyncCompleted = true;
					// getEcosVM() caches its deep clone by reference, and the legacy VM's top-level object
					// reference never changes — force a fresh clone now that the legacy data it wraps just landed.
					iframeDataBridge.invalidateCachedEcosVM();
					log('success', 'DataSync', `✅ Main sync completed for ${event.data.data.app}, starting background sync...`);

					setTimeout(() => {
						this.startBackgroundSync();
					}, 1000);
				}
			}
		});
	}

	hasMainSyncStarted() {
		try {
			// First check if we've received the completion message
			if (this.mainSyncCompleted) {
				return true;
			}

			// Fallback to original check via iframe bridge
			const externis = iframeDataBridge.getExternis(false);
			return externis && (externis.synchronizing['SALFO'] === true || externis.synchronizing['SFA'] === true);
		} catch (error) {
			log('error', 'DataSync', `❌ Failed to check main sync status: ${error.message}`);
			return false;
		}
	}

	async initialize() {
		if (this.isInitialized) return;

		log('info', 'DataSync', '🔄 Initializing DataSync service...');

		try {
			// Charger dynamiquement les modules API seulement si online
			if (iframeDataBridge.isOnline()) {
				const { cleanOldCacheEntries, getCacheStats } = await import('../api/core/file');
				await cleanOldCacheEntries(this.config.maxCacheAge / (24 * 60 * 60 * 1000));

				const stats = await getCacheStats();
				log('info', 'DataSync', `📊 Cache stats: ${JSON.stringify(stats)}`);
			} else {
				log('info', 'DataSync', 'ℹ️ Skipping cache cleanup - offline mode');
			}

			this.isInitialized = true;
			log('success', 'DataSync', '✅ DataSync service initialized');
		} catch (error) {
			log('error', 'DataSync', `❌ Failed to initialize DataSync: ${error.message}`);
		}
	}

	async startBackgroundSync() {
		if (this.syncInProgress) return;

		this.syncInProgress = true;

		try {
			const REFERENCE_SYNC_MIN_INTERVAL = 2 * 60 * 1000;
			const now = Date.now();
			const shouldRunReferenceSync = !this.lastReferenceSyncTs || now - this.lastReferenceSyncTs > REFERENCE_SYNC_MIN_INTERVAL;

			if (shouldRunReferenceSync) {
				await Promise.allSettled([this.syncReferenceData(), this.config.preloadImages ? this.syncAccountImages() : Promise.resolve(), this.syncSellOutDiffusions()]);
				this.lastReferenceSyncTs = now;
			} else {
				log('info', 'DataSync', '⏭️ Skipping reference preload — ran recently (debounced reconnect sync).');
			}

			this.backgroundSyncCompleted = true;
			// Reference data (e.g. productsAssort) can finish loading here, after the main-sync clone was
			// already taken — invalidate so the next getEcosVM() picks up the now-complete catalog.
			iframeDataBridge.invalidateCachedEcosVM();
			this.notifySyncComplete();
			eventBus.emit('background-sync-completed');
			log('success', 'DataSync', '✅ Background sync completed');
		} catch (error) {
			log('error', 'DataSync', `❌ Background sync failed: ${error.message}`);
		} finally {
			this.syncInProgress = false;
		}
	}

	async syncReferenceData() {
		log('info', 'DataSync', '📦 Syncing reference data...');

		const currentUser = iframeDataBridge.getCurrentUser();

		const tasks = [
			{ name: 'UserSettings', fn: () => import('../api/core/userSetting').then((m) => m.getUserSettings()) },
			{
				name: 'UserSettingsWebapp',
				fn: () => (currentUser?.id ? import('../api/core/userSetting').then((m) => m.getUserSettings({ groupName: 'WEBAPP', userId: currentUser.id })) : Promise.resolve()),
			},
			{ name: 'ProductTree', fn: () => import('../api/core/productTree').then((m) => m.getProductTrees()) },
			{ name: 'Categories', fn: () => import('../api/core/category').then((m) => m.getCategories()) },
			{ name: 'CategoryTypes', fn: () => import('../api/core/category').then((m) => m.getCategoryTypes()) },
			{ name: 'AccountRestrictions', fn: () => import('../api/distributor/accountRestriction').then((m) => m.getAccountRestrictions()) },
			{ name: 'Stores', fn: () => import('../api/distributor/store').then((m) => m.getStores()) },
			{ name: 'Editorials', fn: () => import('../api/core/editorial').then((m) => m.getEditorials()) },
			{ name: 'Menus', fn: () => import('../api/core/menu').then((m) => m.getMenus()) },
			{ name: 'Organisations', fn: () => import('../api/core/organisation').then((m) => m.getOrganisations()) },
			{ name: 'OrganisationMaxLevel', fn: () => import('../api/core/organisation').then((m) => m.getOrganisationMaxLevel()) },
			{ name: 'Users', fn: () => import('../api/core/user').then((m) => m.getUsers()) },
			{ name: 'Roles', fn: () => import('../api/core/role').then((m) => m.getRoles()) },
			{ name: 'Campaigns', fn: () => import('../api/core/campaign').then((m) => m.getCampaigns()) },
			{ name: 'Products', fn: () => import('../api/sales/product').then((m) => m.getProducts()) },
			{ name: 'ProductLogistics', fn: () => import('../api/sales/product').then((m) => m.getProductLogistics()) },
			{ name: 'ProductServices', fn: () => import('../api/sales/product').then((m) => m.getProductServices()) },
			{ name: 'Quotas', fn: () => import('../api/sales/quota').then((m) => m.getQuotas()) },
			{ name: 'Providers', fn: () => import('../api/sales/provider').then((m) => m.getProviders()) },
			{ name: 'DeliveryLocationTypes', fn: () => import('../api/sales/deliveryLocation').then((m) => m.getDeliveryLocationTypes()) },
			{ name: 'DeliveryLocations', fn: () => import('../api/sales/deliveryLocation').then((m) => m.getDeliveryLocations()) },
			{ name: 'Waves', fn: () => import('../api/sales/wave').then((m) => m.getWaves()) },
			{ name: 'Shelves', fn: () => import('../api/sales/shelf').then((m) => m.getShelves()) },
			{ name: 'ApplicationDomains', fn: () => import('../api/core/applicationDomain').then((m) => m.getApplicationDomains()) },
			{ name: 'Activities', fn: () => import('../api/sales/activity').then((m) => m.getActivities()) },
			{ name: 'Options', fn: () => import('../api/core/option').then((m) => m.getOptions()) },
			{ name: 'ExcludedDays', fn: () => import('../api/core/option').then((m) => m.getExcludedDays()) },
			{ name: 'Attributes', fn: () => import('../api/core/attribute').then((m) => m.getAttributes()) },
			{ name: 'RolePermissions', fn: () => import('../api/core/role').then((m) => m.getRolePermissions()) },
			{ name: 'CategoryRestrictions', fn: () => import('../api/core/category').then((m) => m.getCategoryRestrictions()) },
			{ name: 'ProductTreeRestrictions', fn: () => import('../api/core/restriction').then((m) => m.getProductTreeRestrictions()) },
			{ name: 'Channels', fn: () => import('../api/distributor/channel').then((m) => m.getChannels()) },
			{ name: 'DeliveryLocationGroups', fn: () => import('../api/sales/deliveryLocationGroup').then((m) => m.getDeliveryLocationGroups()) },
			{ name: 'ContactRoles', fn: () => import('../api/sales/contactRole').then((m) => m.getContactRoles()) },
			{ name: 'DeliveryLocationSeasonalities', fn: () => import('../api/sales/deliveryLocationSeasonality').then((m) => m.getDeliveryLocationSeasonalities()) },
			{ name: 'Surveys', fn: () => import('../api/sales/survey').then((m) => m.getSurveys()) },
			{ name: 'EventTags', fn: () => import('../api/core/eventTag').then((m) => m.getEventTags()) },
			{ name: 'DeliveryLocationsGeo', fn: () => import('../api/sales/deliveryLocation').then((m) => m.getDeliveryLocationsGeo()) },
			{ name: 'DeliveryLocationAttributes', fn: () => import('../api/sales/deliveryLocationAttribute').then((m) => m.getDeliveryLocationAttributes([])) },
			{ name: 'OrganisationWidgets', fn: () => import('../api/core/widget').then((m) => m.getWidgets()) },
			{ name: 'Clusterings', fn: () => import('../api/sales/clustering').then((m) => m.getClusterings()) },
			{ name: 'DistributionChannels', fn: () => import('../api/sales/distributionChannel').then((m) => m.getDistributionChannels()) },
			{ name: 'PosTypes', fn: () => import('../api/sales/posType').then((m) => m.getPosTypes()) },
			{ name: 'DocTemplatesInfo', fn: () => import('../api/communication/docTemplate').then((m) => m.getDocTemplatesInfo()) },
			{ name: 'ProductDocuments', fn: () => import('../api/core/productDocument').then((m) => m.getProductDocuments()) },
			{ name: 'StocksRSW', fn: () => import('../api/sales/stockRsw').then((m) => m.getStocksRSW()) },
			{ name: 'RSWs', fn: () => import('../api/distributor/rsw').then((m) => m.getRSWs()) },
			{ name: 'Companies', fn: () => import('../api/core/company').then((m) => m.getCompanies()) },
			{ name: 'AssortCatalogs', fn: () => import('../api/assort/assort').then((m) => m.getAssortCatalogs()) },
			{ name: 'DeliveryLocationDelays', fn: () => import('../api/sales/deliveryLocation').then((m) => m.getDeliveryLocationDelays()) },
			{ name: 'AssortProducts', fn: () => import('../api/assort/assort').then((m) => m.getAssortProducts()) },
			{ name: 'AssortProductContainers', fn: () => import('../api/assort/assort').then((m) => m.getAssortProductContainers()) },
			{ name: 'AssortProductOrderContainers', fn: () => import('../api/assort/assort').then((m) => m.getAssortProductOrderContainers()) },
			{ name: 'AssortCompetitors', fn: () => import('../api/assort/assort').then((m) => m.getAssortCompetitors()) },
			{ name: 'AssortProductAccounts', fn: () => import('../api/assort/assort').then((m) => m.getAssortProductAccounts()) },
			{ name: 'AccountDocuments', fn: () => import('../api/distributor/accountDocument').then((m) => m.getAccountDocuments()) },
			{ name: 'DeliveryLocationDocuments', fn: () => import('../api/sales/deliveryLocation').then((m) => m.getDeliveryLocationDocuments()) },
			{ name: 'DeliveryLocationRestrictions', fn: () => import('../api/sales/deliveryLocation').then((m) => m.getDeliveryLocationRestrictions()) },
			{ name: 'Extensions', fn: () => import('../api/core/extension').then((m) => m.getExtensions()) },
			{ name: 'ActivePeriod', fn: () => import('../api/core/period').then((m) => m.getActivePeriod()) },
			{ name: 'DeliveryLocationFurnishings', fn: () => import('../api/sales/deliveryLocation').then((m) => m.getDeliveryLocationFurnishings()) },
			{ name: 'ShelfStatementSettings', fn: () => import('../api/sales/shelfStatement').then((m) => m.getShelfStatementSettings()) },
			{ name: 'ShelfStatementConfigurations', fn: () => import('../api/sales/shelfStatement').then((m) => m.getShelfStatementConfigurations()) },
			{ name: 'SfaKPIs', fn: () => import('../api/sales/kpi').then((m) => m.getSfaKPIs()) },
			{ name: 'Promotions', fn: () => import('../api/sales/promotion').then((m) => m.getPromotions()) },
			{ name: 'ProductContainers', fn: () => import('../api/sales/productContainer').then((m) => m.getProductContainers()) },
			{ name: 'AssortUserAgreement', fn: () => import('../api/assort/assort').then((m) => m.getAssortUserAgreement()) },
			{ name: 'ProductTreeCrossRestrictions', fn: () => import('../api/core/restriction').then((m) => m.getProductTreeCrossRestrictions()) },
			{ name: 'AppointmentSubTypes', fn: () => import('../api/sales/appointmentSubType').then((m) => m.getAppointmentSubTypes()) },
			{ name: 'DeliveryLocationSegmentValidations', fn: () => import('../api/sales/deliveryLocation').then((m) => m.getDeliveryLocationSegmentValidations()) },
			{ name: 'AppointmentWorkflows', fn: () => import('../api/sales/appointmentWorkflow').then((m) => m.getAppointmentWorkflows()) },
			{ name: 'SalesPlanReasons', fn: () => import('../api/sales/salesPlan').then((m) => m.getSalesPlanReasons()) },
			{ name: 'UserPoints', fn: () => import('../api/sales/points').then((m) => m.getUserPoints()) },
			{ name: 'PointsHistory', fn: () => import('../api/sales/points').then((m) => m.getPointsHistory()) },
			{ name: 'DirectOrders', fn: () => import('../api/sales/order').then((m) => m.primeDirectOrders()) },
		];

		const results = await Promise.allSettled(tasks.map((t) => t.fn()));

		results.forEach((result, i) => {
			if (result.status === 'rejected') {
				log('warn', 'DataSync', `⚠️ Failed to preload ${tasks[i].name}: ${result.reason?.message}`);
			} else {
				log('success', 'DataSync', `✅ Preloaded ${tasks[i].name}`);
			}
		});
	}

	async syncAccountImages() {
		log('info', 'DataSync', '🖼️ Syncing account images...');

		if (!iframeDataBridge.isOnline()) {
			log('info', 'DataSync', 'ℹ️ Skipping account image sync - offline mode');
			return;
		}

		try {
			const accounts = await this.fetchAccounts();

			if (!accounts || accounts.length === 0) {
				log('info', 'DataSync', 'ℹ️ No accounts found for image sync');
				return;
			}

			const imageIds = [...new Set(accounts.filter((account) => account.imageUId).map((account) => account.imageUId))];

			log('info', 'DataSync', `📥 Found ${imageIds.length} account images to sync`);

			await this.syncImagesBatch(imageIds);

			imageIds.forEach((id) => this.accountImages.add(id));
		} catch (error) {
			log('error', 'DataSync', `❌ Failed to sync account images: ${error.message}`);
		}
	}

	async syncSellOutDiffusions() {
		log('info', 'DataSync', '📊 Syncing sell-out diffusions...');

		if (!iframeDataBridge.isOnline()) {
			log('info', 'DataSync', 'ℹ️ Skipping sell-out diffusions sync - offline mode');
			return;
		}

		const deliveryLocationIds = this.getDeliveryLocationIds();
		if (deliveryLocationIds.length === 0) {
			log('info', 'DataSync', 'ℹ️ No delivery locations found for sell-out diffusions sync');
			return;
		}

		try {
			const { preloadAllProductDeliveryLocationDiffusions } = await import('../api/assort/productDeliveryLocationDiffusion');
			await preloadAllProductDeliveryLocationDiffusions(deliveryLocationIds);

			log('success', 'DataSync', `✅ Sell-out diffusions preloaded for ${deliveryLocationIds.length} delivery locations in one bulk request`);
		} catch (error) {
			log('error', 'DataSync', `❌ Failed to sync sell-out diffusions: ${error.message}`);
		}
	}

	async syncImagesBatch(imageIds) {
		const batches = this.createBatches(imageIds, this.config.batchSize);

		for (let i = 0; i < batches.length; i++) {
			const batch = batches[i];
			log('info', 'DataSync', `📦 Processing batch ${i + 1}/${batches.length} (${batch.length} images)`);

			const promises = batch.map((imageId) => this.syncImageWithRetry(imageId));

			const results = await Promise.allSettled(promises);

			const successful = results.filter((r) => r.status === 'fulfilled').length;
			const failed = results.filter((r) => r.status === 'rejected').length;

			if (failed > 0) {
				log('warn', 'DataSync', `⚠️ Batch ${i + 1}: ${successful} successful, ${failed} failed`);
			} else {
				log('success', 'DataSync', `✅ Batch ${i + 1}: ${successful} images synced`);
			}

			if (i < batches.length - 1) {
				await this.delay(100);
			}
		}
	}

	async syncImageWithRetry(imageId) {
		for (let attempt = 1; attempt <= this.config.maxRetries; attempt++) {
			try {
				const { prefetchFile } = await import('../api/core/file');
				await prefetchFile(imageId);
				return imageId;
			} catch (error) {
				if (attempt === this.config.maxRetries) {
					log('error', 'DataSync', `❌ Failed to sync image ${imageId} after ${attempt} attempts: ${error.message}`);
					throw error;
				}

				await this.delay(this.config.retryDelay * attempt);
			}
		}
	}

	async fetchAccounts() {
		try {
			const { getAccounts } = await import('../api/distributor/account');
			return await getAccounts();
		} catch (error) {
			log('error', 'DataSync', `❌ Failed to fetch accounts: ${error.message}`);
			return [];
		}
	}

	async preloadAccountImage(fileId) {
		if (!fileId || this.accountImages.has(fileId)) return;

		try {
			const { prefetchFile } = await import('../api/core/file');
			await prefetchFile(fileId);
			this.accountImages.add(fileId);
			log('success', 'DataSync', `✅ Preloaded image for account ${fileId}`);
		} catch (error) {
			log('error', 'DataSync', `❌ Failed to preload image for account ${fileId}: ${error.message}`);
		}
	}

	onSyncComplete(callback) {
		this.syncCallbacks.push(callback);
	}

	notifySyncComplete() {
		this.syncCallbacks.forEach((callback) => {
			try {
				callback();
			} catch (error) {
				log('error', 'DataSync', `❌ Sync callback error: ${error.message}`);
			}
		});
	}

	async forceSync() {
		if (this.syncInProgress) {
			log('info', 'DataSync', '⏳ Sync already in progress...');
			return;
		}

		log('info', 'DataSync', '🔄 Force syncing data...');
		this.lastReferenceSyncTs = 0;
		await this.startBackgroundSync();
	}

	async clearCache() {
		log('info', 'DataSync', '🗱️ Clearing all cached data...');

		try {
			const { clearFileCache } = await import('../api/core/file');
			await clearFileCache();
			this.accountImages.clear();
			this.criticalData.clear();

			log('success', 'DataSync', '✅ Cache cleared successfully');
		} catch (error) {
			log('error', 'DataSync', `❌ Failed to clear cache: ${error.message}`);
		}
	}

	isSyncCompleted() {
		try {
			return this.mainSyncCompleted;
		} catch {
			return false;
		}
	}

	isBackgroundSyncCompleted() {
		try {
			return this.backgroundSyncCompleted;
		} catch {
			return false;
		}
	}

	getSyncStatus() {
		return {
			isInitialized: this.isInitialized,
			syncInProgress: this.syncInProgress,
			cachedImages: this.accountImages.size,
			criticalDataEntries: this.criticalData.size,
			mainSyncCompleted: this.mainSyncCompleted,
		};
	}

	getCachedData(key) {
		const entry = this.criticalData.get(key);
		if (!entry) return null;

		const age = Date.now() - entry.timestamp;
		if (age > this.config.maxCacheAge) {
			this.criticalData.delete(key);
			return null;
		}

		return entry.data;
	}

	getDeliveryLocationIds() {
		try {
			const deliveryLocations = iframeDataBridge.getEcosVM()?.deliveryLocations;
			if (!Array.isArray(deliveryLocations) || deliveryLocations.length === 0) return [];
			return deliveryLocations.map((dl) => dl.id).filter(Boolean);
		} catch (error) {
			log('warn', 'DataSync', `⚠️ Failed to retrieve delivery location IDs: ${error.message}`);
			return [];
		}
	}

	createBatches(array, batchSize) {
		const batches = [];
		for (let i = 0; i < array.length; i += batchSize) {
			batches.push(array.slice(i, i + batchSize));
		}
		return batches;
	}

	delay(ms) {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}
}

const dataSync = new DataSyncService();
export default dataSync;

export const initializeSync = () => dataSync.initialize();
export const forceSync = () => dataSync.forceSync();
export const preloadAccountImage = (accountId) => dataSync.preloadAccountImage(accountId);
export const clearSyncCache = () => dataSync.clearCache();
export const getSyncStatus = () => dataSync.getSyncStatus();
export const isSyncCompleted = () => dataSync.isSyncCompleted();
export const isBackgroundSyncCompleted = () => dataSync.isBackgroundSyncCompleted();
export const onSyncComplete = (callback) => dataSync.onSyncComplete(callback);
export const getCachedAccountData = () => dataSync.getCachedData('accounts');
